import type { AgentStatusChangedPayload } from "@shared/pusher-events";
import { replaceEqualDeep, type QueryClient } from "@tanstack/react-query";

export type EndpointAgentStatus = {
  endpointId: string;
  tenantId?: string | null;
  status: string;
  customLabel: string | null;
  updatedAt: string | null;
  statusVersion?: number;
};

/** Pusher is authoritative; a pending initial/refetch response may be older. */
export function reconcileEndpointAgentStatus(
  previous: EndpointAgentStatus | undefined, fetched: EndpointAgentStatus,
): EndpointAgentStatus {
  if (!previous || previous.endpointId !== fetched.endpointId ||
      (previous.tenantId && fetched.tenantId && previous.tenantId !== fetched.tenantId)) {
    return replaceEqualDeep(previous, fetched);
  }
  if (!newerThan(previous, { ...fetched, tenantId: fetched.tenantId ?? previous.tenantId ?? "" })) {
    return replaceEqualDeep(previous, { ...fetched, status: previous.status,
      customLabel: previous.customLabel, updatedAt: previous.updatedAt, statusVersion: previous.statusVersion });
  }
  return replaceEqualDeep(previous, fetched);
}

/** Also seeds an uninitialized sidebar so the first event isn't discarded. */
export function applyEndpointAgentStatus(client: QueryClient, event: AgentStatusChangedPayload): void {
  client.setQueryData<EndpointAgentStatus>(["/api/agent-statuses", event.endpointId], previous => {
    if (previous?.tenantId && previous.tenantId !== event.tenantId) return previous;
    if (!newerThan(previous ?? null, event)) return previous;
    return { ...previous, endpointId: event.endpointId, tenantId: event.tenantId,
      status: event.status, customLabel: event.status === "ready" ? null : event.customLabel ?? null,
      updatedAt: event.updatedAt, statusVersion: event.statusVersion ?? previous?.statusVersion };
  });
}

export type LiveAgentUser = {
  id: string;
  tenantId: string | null;
  plivoEndpointId: string | null;
  endpoint?: { id: string } | null;
  agentStatus: {
    status: string;
    customLabel: string | null;
    updatedAt: string | null;
    statusVersion?: number;
  } | null;
};

export function matchesAgent(user: LiveAgentUser, event: AgentStatusChangedPayload): boolean {
  return user.tenantId === event.tenantId
    && (user.plivoEndpointId ?? user.endpoint?.id) === event.endpointId;
}

function newerThan(current: LiveAgentUser["agentStatus"], event: AgentStatusChangedPayload): boolean {
  if (!current) return true;
  if (current.statusVersion != null && event.statusVersion != null) {
    return event.statusVersion > current.statusVersion;
  }
  const oldTime = Date.parse(current.updatedAt ?? "");
  const newTime = Date.parse(event.updatedAt ?? "");
  if (Number.isFinite(oldTime) && (!Number.isFinite(newTime) || newTime < oldTime)) return false;
  return current.status !== event.status || current.customLabel !== event.customLabel
    || current.updatedAt !== event.updatedAt || current.statusVersion !== event.statusVersion;
}

/** Apply committed events only to their tenant/endpoint; ignore duplicate/stale versions. */
export function patchLiveAgentUsers<T extends LiveAgentUser>(
  users: T[] | undefined, event: AgentStatusChangedPayload,
): T[] | undefined {
  if (!users) return users;
  let changed = false;
  const updated = users.map(user => {
    if (!matchesAgent(user, event) || !newerThan(user.agentStatus, event)) return user;
    changed = true;
    return { ...user, agentStatus: {
      ...user.agentStatus, status: event.status,
      customLabel: event.status === "ready" ? null : event.customLabel ?? null,
      updatedAt: event.updatedAt, statusVersion: event.statusVersion ?? user.agentStatus?.statusVersion,
    } };
  });
  return changed ? updated : users;
}

/** A request started before a push must not overwrite that newer committed status. */
export function reconcileLiveAgentUsers<T extends LiveAgentUser>(previous: T[] | undefined, fetched: T[]): T[] {
  const merged = fetched.map(user => {
    const cached = previous?.find(old => old.id === user.id && old.tenantId === user.tenantId
      && (old.plivoEndpointId ?? old.endpoint?.id) === (user.plivoEndpointId ?? user.endpoint?.id));
    if (!cached?.agentStatus) return user;
    if (!user.agentStatus || (cached.agentStatus.statusVersion != null
      && user.agentStatus.statusVersion != null && cached.agentStatus.statusVersion > user.agentStatus.statusVersion)) {
      return { ...user, agentStatus: cached.agentStatus };
    }
    return user;
  });
  return replaceEqualDeep(previous, merged);
}
